import { StatusBadge } from "./StatusBadge"
import { Tooltip } from "./Tooltip"
import { truncate } from "lodash"

import type { JSX } from "react"
import { type StatusColor } from "~/constants/status"
import { Badge } from "~/components/reusable/Badge"
import ProductImage from "~/components/reusable/ProductImage"

interface Props {
  title: string
  titleIcon?: React.ReactNode
  value?: string
  /** A `Badge` here must be `size="sm"` (20px, the row height); the default 26px badge breaks the row spacing. */
  valueComponent?: React.ReactNode
  valueImage?: string
  /** Round person avatar in place of the square product image — for user-valued rows. */
  valueAvatar?: React.ReactNode
  valueIcon?: React.ReactNode
  tooltip?: string
  valueTooltip?: JSX.Element
  list?: string[]
  statusColor?: StatusColor
  alignment?: "left" | "center" | "right"
  noTruncate?: boolean
  badge?: string
}

const InformationRow = ({
  title,
  titleIcon,
  value,
  valueComponent,
  valueImage,
  valueAvatar,
  valueIcon,
  tooltip,
  list,
  statusColor,
  valueTooltip,
  alignment = "left",
  noTruncate = false,
  badge,
}: Props) => {
  return (
    <>
      <Tooltip title={tooltip} className="max-w-[300px]" disableHoverableContent>
        <div className="flex items-center gap-2 [&_svg]:fill-fg-muted">
          {titleIcon && titleIcon}
          <dt className="label-base text-fg-subtle">{title}</dt>
        </div>
      </Tooltip>

      {!list ? (
        statusColor === undefined ? (
          <dd className={`paragraph-base text-${alignment} min-w-32 flex items-center gap-1.5`}>
            {valueAvatar
              ? valueAvatar
              : valueImage !== undefined && (
                  <div className="shrink-0">
                    <ProductImage url={valueImage} alt={title} width={20} />
                  </div>
                )}
            {valueIcon && (
              <span className="flex items-center [&_svg]:text-fg-muted">{valueIcon}</span>
            )}
            {valueComponent ? (
              valueComponent
            ) : (
              <Tooltip
                title={!valueTooltip && value && value.length > 23 ? value : ""}
                body={valueTooltip}
              >
                <div className="label-base">
                  {value ? (noTruncate ? value : truncate(value, { length: 23 })) : "-"}
                </div>
              </Tooltip>
            )}

            {badge && (
              <Badge variant="neutral" size="sm">
                {badge}
              </Badge>
            )}
          </dd>
        ) : (
          <StatusBadge color={statusColor} text={value ?? "-"} />
        )
      ) : (
        <dd className={`paragraph-base text-${alignment} min-w-32`}>
          <ul>
            {list.map((item, index) => (
              <Tooltip key={index} title={item.length > 23 ? item : ""}>
                <li>{noTruncate ? item : truncate(item, { length: 23 })}</li>
              </Tooltip>
            ))}
          </ul>
        </dd>
      )}
    </>
  )
}

export default InformationRow
